import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { AlertTriangle, Check, Info, X } from "lucide-react";

// 輕量提示。訊息不可只靠顏色分辨,每則都帶圖示與「成功 / 提醒 / 錯誤」文字。

export type ToastTone = "success" | "info" | "error";

export interface ToastMessage {
  id: number;
  tone: ToastTone;
  text: string;
}

interface ToastContextValue {
  /** 推一則提示,回傳它的 id。 */
  push: (text: string, tone?: ToastTone) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TONE_LABEL: Record<ToastTone, string> = {
  success: "成功",
  info: "提醒",
  error: "錯誤",
};

const TONE_CLASS: Record<ToastTone, string> = {
  success: "border-[var(--ok)] text-[var(--ok)]",
  info: "border-[var(--accent)] text-[var(--accent-hover)]",
  error: "border-[var(--danger)] text-[var(--danger)]",
};

function ToneIcon({ tone }: { tone: ToastTone }) {
  if (tone === "success") return <Check aria-hidden="true" size={16} />;
  if (tone === "error") return <AlertTriangle aria-hidden="true" size={16} />;
  return <Info aria-hidden="true" size={16} />;
}

/** 預設 4 秒後自動收起,錯誤留久一點讓人看得完。 */
const AUTO_DISMISS_MS: Record<ToastTone, number> = {
  success: 4000,
  info: 4000,
  error: 8000,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastMessage[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const push = useCallback(
    (text: string, tone: ToastTone = "info") => {
      const id = nextId.current++;
      setItems((prev) => [...prev, { id, tone, text }]);
      window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS[tone]);
      return id;
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* 提示區固定在右上,螢幕閱讀器以 polite 播報,不打斷使用者。 */}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed right-4 top-4 z-[60] flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-2"
      >
        {items.map((item) => (
          <div
            key={item.id}
            role={item.tone === "error" ? "alert" : "status"}
            className={
              "pointer-events-auto flex items-start gap-2 rounded-[var(--radius)] border bg-[var(--surface)] " +
              `px-3 py-2 text-sm shadow-card ${TONE_CLASS[item.tone]}`
            }
          >
            <ToneIcon tone={item.tone} />
            <span className="min-w-0 flex-1 text-[var(--text)]">
              <span className="mr-1 font-medium">{TONE_LABEL[item.tone]}:</span>
              {item.text}
            </span>
            <button
              type="button"
              onClick={() => dismiss(item.id)}
              aria-label="關閉提示"
              className="rounded-[var(--radius)] p-0.5 text-[var(--text-3)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
            >
              <X aria-hidden="true" size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** 取得推提示的函式。沒有 Provider 時退回 no-op,元件單獨測試不會炸。 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  return ctx ?? { push: () => 0, dismiss: () => undefined };
}
